import React, { useEffect, useState } from 'react';
import './LoadingSpinner.css';

export interface LoadingSpinnerProps {
  message: string;
  estimatedTime?: number; // in seconds, default 20
}

const STYLE_STEPS = [
  { icon: '◆', text: 'Stilinizi analiz ediyoruz...' },
  { icon: '◇', text: 'Renk paletinizi oluşturuyoruz...' },
  { icon: '◆', text: 'Trendleri inceliyoruz...' },
  { icon: '◇', text: 'Kombinleri deniyoruz...' },
  { icon: '◆', text: 'Son rötuşlar...' },
];

const FASHION_TIPS = [
  { tip: 'Beyaz gömlek, gardırobun en çok yönlü parçasıdır.' },
  { tip: '3 renk kuralı: Bir kombin için ideal renk sayısı.' },
  { tip: 'Aksesuarlar, sade bir kıyafeti bile şık gösterir.' },
  { tip: 'Bedeninize uygun kesimler her zaman daha şık durur.' },
  { tip: 'Katmanlı giysiler, hem şık hem pratiktir.' },
  { tip: 'İyi bir çanta, yatırım yapmaya değer.' },
  { tip: 'Komplementer renkler dikkat çeker, analog renkler uyum sağlar.' },
];

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ 
  message, 
  estimatedTime = 20 
}) => {
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [currentTip, setCurrentTip] = useState(0);
  const [pulseActive, setPulseActive] = useState(false);

  // Progress animation
  useEffect(() => {
    const stepDuration = (estimatedTime * 1000) / 100;
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 99) return 99;
        return prev + 1;
      });
    }, stepDuration);

    return () => clearInterval(interval);
  }, [estimatedTime]);

  // Step progression based on progress
  useEffect(() => {
    const stepThresholds = [0, 20, 40, 60, 80];
    const newStep = stepThresholds.findIndex((threshold, index) => {
      const nextThreshold = stepThresholds[index + 1] || 100;
      return progress >= threshold && progress < nextThreshold;
    });
    
    if (newStep !== currentStep && newStep !== -1) {
      setPulseActive(true);
      setCurrentStep(newStep);
      setTimeout(() => setPulseActive(false), 500);
    }
  }, [progress, currentStep]);

  // Fashion tip rotation
  useEffect(() => {
    const tipInterval = setInterval(() => {
      setCurrentTip(prev => (prev + 1) % FASHION_TIPS.length);
    }, 4000);

    return () => clearInterval(tipInterval);
  }, []);

  return (
    <div className="loading-page-container">
      <div className="loading-page-content">
        {/* Elegant Geometric Animation */}
        <div className="geometric-animation">
          {/* Rotating Rings */}
          <div className="ring ring-outer"></div>
          <div className="ring ring-middle"></div>
          <div className="ring ring-inner"></div>
          
          {/* Center Diamond */}
          <div className="center-diamond">
            <div className="diamond-shape"></div>
          </div>

          {/* Orbiting Dots */}
          <div className="orbit-container">
            <div className="orbit-dot dot-1"></div>
            <div className="orbit-dot dot-2"></div>
            <div className="orbit-dot dot-3"></div>
            <div className="orbit-dot dot-4"></div>
          </div>
        </div>

        {/* Current Step Display */}
        <div className={`step-display ${pulseActive ? 'pulse' : ''}`}>
          <span className="step-icon">{STYLE_STEPS[currentStep].icon}</span>
          <span className="step-text">{STYLE_STEPS[currentStep].text}</span>
        </div>

        {/* Progress Bar */}
        <div className="progress-section">
          <div className="progress-bar-container">
            <div 
              className="progress-bar-fill"
              style={{ width: `${progress}%` }}
            >
              <div className="progress-shimmer"></div>
            </div>
            <div className="progress-milestone-markers">
              {[20, 40, 60, 80].map((mark) => (
                <div 
                  key={mark}
                  className={`milestone ${progress >= mark ? 'active' : ''}`}
                  style={{ left: `${mark}%` }}
                />
              ))}
            </div>
          </div>
          <div className="progress-percentage">{progress}%</div>
        </div>

        {/* Loading Message */}
        <div className="loading-message">
          <h3>{message}</h3>
          <div className="message-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>

        {/* Fashion Tip Section */}
        <div className="tip-section">
          <div className="tip-divider">
            <span className="divider-line"></span>
            <span className="divider-icon">◆</span>
            <span className="divider-line"></span>
          </div>
          <div className="tip-content" key={currentTip}>
            <p className="tip-text">"{FASHION_TIPS[currentTip].tip}"</p>
          </div>
          <div className="tip-dots">
            {FASHION_TIPS.map((_, i) => (
              <span 
                key={i} 
                className={`tip-dot ${i === currentTip ? 'active' : ''}`}
              />
            ))}
          </div>
        </div>

        {/* Decorative Lines */}
        <div className="decorative-lines">
          <div className="line line-1"></div>
          <div className="line line-2"></div>
          <div className="line line-3"></div>
          <div className="line line-4"></div>
        </div>
      </div>

      {/* Subtle Grid Pattern */}
      <div className="bg-grid"></div>
    </div>
  );
};

export default LoadingSpinner;
